import React, { useEffect, useRef, useState } from "react";
import { LANDING } from "@/constants/testIds";
import { X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const CODE = "DUBAI10";

export default function ExitPopup() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // 24h TTL — once seen, snooze for 24h instead of forever
    const seenAt = parseInt(localStorage.getItem("dp_exit_seen_at") || "0", 10);
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (seenAt && Date.now() - seenAt < ONE_DAY_MS) {
      shownRef.current = true;
      return;
    }

    const trigger = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      setOpen(true);
      localStorage.setItem("dp_exit_seen_at", String(Date.now()));
    };

    const onMouseLeave = (e) => {
      if (e.clientY <= 5) trigger();
    };

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    let timeoutId;
    if (isMobile) {
      timeoutId = setTimeout(trigger, 30000);
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      localStorage.setItem("dp_discount", CODE);
      window.dispatchEvent(new Event("dp:discount-changed"));
      setCopied(true);
      toast.success(`Code ${CODE} copied · auto-applied at checkout`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Please copy manually: " + CODE);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid={LANDING.exitPopup}
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 f-up"
    >
      <div className="relative bg-white rounded-3xl max-w-md w-full p-8 md:p-10 shadow-2xl">
        <button
          data-testid={LANDING.exitPopupClose}
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#f4f4f3] hover:bg-[#ececec] flex items-center justify-center"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="text-center">
          <div className="sticker sticker-lime inline-block text-[12px] mb-5">
            🎁 One-time offer
          </div>
          <h3 className="text-[26px] md:text-[32px] font-black text-[#0e0e10] leading-tight">
            Wait — don&apos;t leave just yet.
          </h3>
          <p className="mt-3 text-[#5a5a60] text-[14px] md:text-[15px]">
            Get <span className="text-[#0e0e10] font-bold">10% off</span> your first order. Tap the code to copy — auto-applied at checkout.
          </p>

          <button
            type="button"
            onClick={copy}
            data-testid="exit-popup-copy"
            className={`mt-6 w-full flex items-center justify-between gap-3 rounded-full px-5 py-4 font-extrabold transition ${
              copied
                ? "bg-[#22c55e] text-white"
                : "bg-[#0e0e10] text-white hover:bg-[#d8f06b] hover:text-[#0e0e10]"
            }`}
          >
            <span className="text-[18px] tabular-nums tracking-[0.18em]">
              {CODE}
            </span>
            <span className="text-[12px] uppercase tracking-wider inline-flex items-center gap-1.5">
              {copied ? (
                <>
                  <Check size={14} /> Copied
                </>
              ) : (
                <>
                  <Copy size={14} /> Tap to copy
                </>
              )}
            </span>
          </button>

          <p className="mt-4 text-[11px] text-[#8a8a8a]">
            Saved locally · no email, no spam, ever.
          </p>
        </div>
      </div>
    </div>
  );
}
